import React, {useState} from 'react';
import {pdf} from '@react-pdf/renderer';

import {useFeedbackTools} from "../../../../components/FeedbackTools";

import SectionCheckInAlert from "./SectionCheckInAlert";
import GuestsList from "./GuestsList";
import PdfGuestList from "./PdfGuestsList";

import Stack from "@mui/material/Stack";

import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {DateFormatter} from "../../../../services/date";
import Box from "@mui/material/Box";
import GuestsMenu from "./GuestsMenu";
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import IconButton from "@mui/material/IconButton";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import useDialog from "../../../../hooks/useDialog";
import FormGuest from "./FormGuest";
import {useGuestCreate, useGuestUpdate, useGuestDelete} from "../../query";
import FormCheckIn from "./FormCheckIn";
import CheckInsList from "./CheckInsList";
import FormCheckOut from "./FormCheckOut";


const GuestsTab = ({booking, accommodations}) => {
    const {showSuccessNotification, showErrorNotification, askConfirmation} = useFeedbackTools();
    const [pdfLoading, setPdfLoading] = useState(false);

    const formCreateGuest = useDialog();
    const formCheckIn = useDialog();
    const formCheckOut = useDialog();
    const [guestOnEditing, setGuestOnEditing] = useState(null);

    const guestCreate = useGuestCreate({bookingId: booking.id});
    const guestUpdate = useGuestUpdate({bookingId: booking.id});
    const guestDelete = useGuestDelete({bookingId: booking.id});

    const checkInOpen = booking.checkInOpen();

    const handleGuestCreate = async (data) => {
        await guestCreate.mutateAsync(data);
        formCreateGuest.close();
    }

    const handleGuestEdit = (guest) => {
        setGuestOnEditing(guest);
    }

    const handleGuestUpdate = async (data) => {
        await guestUpdate.mutateAsync({guestId: guestOnEditing.id, ...data});
        setGuestOnEditing(null);
    }

    const handleGuestDelete = (guest) => {
        askConfirmation(
            {
                title: "Elimina ospite",
                description: `Confermi l'eliminazione dell'ospite selezionato?`,
                confirmationText: 'Si',
                cancellationText: 'No',
                onConfirm: async () => {
                    try {
                        await guestDelete.mutateAsync({guestId: guest.id});
                        showSuccessNotification("Ospite eliminato con successo");
                    } catch (e) {
                        showErrorNotification(e);
                    }
                }
            },
        );
    }

    const generateGuestsPDF = async () => {
        setPdfLoading(true);
        try {
            const blob = await pdf(<PdfGuestList booking={booking}
                                                 findAccommodationUnit={accommodations.findAccommodationUnit}/>).toBlob();
            const fileURL = URL.createObjectURL(blob);
            const tempLink = document.createElement('a');
            tempLink.href = fileURL;
            tempLink.setAttribute('download', `lista-ospiti-${booking.codeNumber}.pdf`);
            tempLink.click();
        } catch (e) {
            console.error(e);
            showErrorNotification("Si è verificato un errore durante la creazione del documento");
        }
        setPdfLoading(false);
    }

    return <>
        <Stack sx={{p: 2}} spacing={2}>
            <SectionCheckInAlert booking={booking} />
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{flexGrow: 1}} variant='subtitle2'>
                    Elenco Ospiti
                </Typography>
                <IconButton size="small" onClick={formCreateGuest.open} disabled={!booking.isEditable()}>
                    <PersonAddAltIcon/>
                </IconButton>
                <IconButton disabled={booking.guests.length === 0 || pdfLoading}
                            size="small"
                            onClick={generateGuestsPDF}>
                    <PictureAsPdfIcon/>
                </IconButton>
                <GuestsMenu booking={booking}/>
            </Stack>

            <GuestsList booking={booking}
                        onGuestDelete={handleGuestDelete}
                        onGuestEdit={handleGuestEdit}/>

            <Stack direction="row" spacing={2} justifyContent="center" alignItems='center'>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3}}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!booking.checkOutEnabled() || !booking.isEditable()}
                        color="primary"
                        onClick={formCheckOut.open}>
                        Check out
                    </Button>
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3}}>
                    <Button
                        disabled={!checkInOpen || !booking.isEditable()}
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={formCheckIn.open}>
                        Nuovo Check-In
                    </Button>
                </Box>
            </Stack>
            {
                !checkInOpen && !booking.isInThePast() && !booking.todayIsTheLastDay() &&
                <Typography variant="body2" color='text.disabled' sx={{mt: 1}} textAlign="center">
                    (Il check-in si aprirà in data {DateFormatter.longString(booking.getArrival())})
                </Typography>
            }

            {
                booking.isCheckInDone() && <>
                    <Typography sx={{pt: 2}} variant='subtitle2'>
                        Check-In effettuati
                    </Typography>

                    <CheckInsList booking={booking}/>
                </>
            }
        </Stack>

        {
            formCreateGuest.show && <FormGuest booking={booking}
                                               isLoading={guestCreate.isLoading}
                                               onSaveGuest={handleGuestCreate}
                                               onClose={formCreateGuest.close}/>
        }

        {
            guestOnEditing !== null && guestOnEditing !== undefined &&
            <FormGuest booking={booking}
                       guest={guestOnEditing}
                       isLoading={guestUpdate.isLoading}
                       onSaveGuest={handleGuestUpdate}
                       onClose={() => {
                           setGuestOnEditing(null);
                       }}/>
        }

        {
            formCheckIn.show && <FormCheckIn open={!formCreateGuest.show && !guestOnEditing}
                                             onClose={formCheckIn.close}
                                             booking={booking}
                                             onNewGuest={formCreateGuest.open}
                                             onEditGuest={handleGuestEdit} />
        }

        {
            formCheckOut.show && <FormCheckOut onClose={formCheckOut.close}
                                               booking={booking} />
        }
    </>
}

GuestsTab.propTypes = {
    booking: PropTypes.object.isRequired,
    accommodations: PropTypes.object.isRequired,
}

export default GuestsTab;